import { useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn, BtnIconOnly, InputField, IconTrash } from '../ui';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import type { PMServiceLevel } from '../../types/pmTypes';
import {
    draftsFromServiceLevels,
    type ServiceLevelDraft,
    validateServiceLevelDrafts,
} from './editServiceLevels';
import '../../css/PMEquipmentConfiguration.css';

interface EditServiceLevelsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (rows: { id: string; levelIdentifier: string; intervalValue: number }[]) => Promise<void>;
    onRemoveLevel?: (level: { id: string; name: string }) => void;
    levels: Pick<PMServiceLevel, 'id' | 'levelIdentifier' | 'intervalValue'>[];
    unitOfMeasure: string;
}

export const EditServiceLevelsModal = ({
    isOpen,
    onClose,
    onSave,
    onRemoveLevel,
    levels,
    unitOfMeasure,
}: EditServiceLevelsModalProps) => {
    const umLabel = displayUnitOfMeasure(unitOfMeasure);
    const [rows, setRows] = useState<ServiceLevelDraft[]>(() => draftsFromServiceLevels(levels));
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [wasOpen, setWasOpen] = useState(isOpen);

    if (isOpen !== wasOpen) {
        setWasOpen(isOpen);
        if (isOpen) {
            setRows(draftsFromServiceLevels(levels));
            setError('');
            setSaving(false);
        }
    }

    const handleClose = () => {
        if (saving) return;
        setError('');
        onClose();
    };

    const updateRow = (id: string, patch: Partial<ServiceLevelDraft>) => {
        setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    };

    const handleSave = async () => {
        const validationMessage = validateServiceLevelDrafts(rows);
        if (validationMessage) {
            setError(validationMessage);
            return;
        }
        setSaving(true);
        setError('');
        try {
            await onSave(
                rows.map((row) => ({
                    id: row.id,
                    levelIdentifier: row.levelIdentifier.trim(),
                    intervalValue: parseFloat(row.intervalValue),
                })),
            );
            onClose();
        } catch (err) {
            console.error('Failed to save service levels:', err);
            setError('Failed to save level changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Edit Service Levels"
            size="large"
            preferBodyFocus
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonOnClick={handleClose}
                        buttonDisabled={saving}
                    />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : 'Save'}
                        buttonOnClick={() => {
                            void handleSave();
                        }}
                        buttonDisabled={saving || rows.length === 0}
                    />
                </div>
            }
        >
            {error && (
                <p className="pm-modal-error" role="alert">
                    {error}
                </p>
            )}

            {rows.length === 0 ? (
                <p className="pm-modal-hint">No service levels to edit yet. Add a level first.</p>
            ) : (
                <div className="pm-edit-levels-list">
                    {rows.map((row, index) => (
                        <div key={row.id} className="pm-modal-row pm-edit-levels-row">
                            <InputField
                                label="Level name"
                                value={row.levelIdentifier}
                                onChange={(value) => updateRow(row.id, { levelIdentifier: value })}
                                placeholder="e.g. Level 2"
                                autoFocus={index === 0}
                                inputId={`edit-level-name-${row.id}`}
                                ariaLabel={`Level name ${index + 1}`}
                            />
                            <InputField
                                label="Interval"
                                type="number"
                                min={1}
                                value={row.intervalValue}
                                onChange={(value) => updateRow(row.id, { intervalValue: value })}
                                placeholder="4000"
                                suffix={umLabel}
                                inputId={`edit-level-interval-${row.id}`}
                                ariaLabel={`Interval for ${row.levelIdentifier || `level ${index + 1}`}`}
                            />
                            {onRemoveLevel && (
                                <BtnIconOnly
                                    size="sm"
                                    variant="danger"
                                    icon={<IconTrash />}
                                    ariaLabel={`Remove ${row.levelIdentifier || `level ${index + 1}`}`}
                                    onClick={() =>
                                        onRemoveLevel({
                                            id: row.id,
                                            name: row.levelIdentifier.trim() || `level ${index + 1}`,
                                        })
                                    }
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </CustomModal>
    );
};
